import { RankKit } from "@/components/site/RankKit";
import { createFileRoute } from "@tanstack/react-router";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { TableOfContents, Section, SectionHeader, CTABand, FAQ, StatRow, Breadcrumbs, InlineCTA, FeatureGrid } from "@/components/site/Blocks";
import { TLDR, ProsCons, DscrCalculator, ExpertInsight } from "@/components/site/SeoBlocks";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { DscrProgramLinks } from "@/components/site/DscrProgramLinks";
import { DscrCrossProductLinks } from "@/components/site/DscrCrossProductLinks";
import { getDscrClusterHero } from "@/data/dscr-cluster-heroes";
import { buildHead } from "@/lib/seo-head";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

const URL = "/interest-only-dscr-loans";
const HERO = getDscrClusterHero(URL);

const KEYWORDS = [
  "interest only DSCR loan",
  "interest only rental property loan",
  "10 year interest only DSCR",
  "interest only investment property mortgage",
  "DSCR loan interest only option",
  "interest only DSCR loan requirements",
  "interest only DSCR loan rates",
  "IO period rental loan",
  "40 year interest only DSCR",
  "interest only cash flow strategy",
  "DSCR interest only vs amortizing",
  "interest only DSCR loan Florida",
].join(", ");

const FAQS = [
  {
    q: "How does an interest-only DSCR loan work?",
    a: "For an initial period — commonly 10 years on a 30-year note, or 10 years on a 40-year term — you pay interest only. Payments are lower, so the DSCR calculation improves. After the IO period the loan amortizes over the remaining term and the payment steps up.",
  },
  {
    q: "Does interest-only improve my DSCR?",
    a: "Yes. Most programs qualify the file on the interest-only payment, which lowers PITIA and raises the ratio. That can move a marginal deal into a better pricing tier or unlock leverage the amortizing payment would not support.",
  },
  {
    q: "Is the rate higher on an interest-only DSCR loan?",
    a: "Usually there is a modest adjustment versus the fully amortizing version of the same program. The exact add-on depends on FICO, LTV, and the length of the IO period.",
  },
  {
    q: "What happens when the interest-only period ends?",
    a: "The loan recasts and amortizes over the remaining term, so the payment increases — often meaningfully. Most investors plan to refinance, sell, or absorb the step-up with rent growth before that date.",
  },
  {
    q: "What are the credit and leverage requirements?",
    a: "Interest-only availability generally starts a tier above the standard program: expect a higher minimum FICO and slightly lower maximum LTV than the amortizing equivalent, with the same 1–6 months of reserves.",
  },
  {
    q: "Can I get interest-only on a cash-out refinance?",
    a: "On many programs, yes, though leverage is lower than on a purchase. It is a common structure for pulling equity out while keeping the new payment close to the old one.",
  },
  {
    q: "Is an interest-only DSCR loan available on a 15-year term?",
    a: "Interest-only structures on DSCR programs are typically paired with 30-year or 40-year terms, not 15-year notes, since the point of the feature is to lower the payment and maximize the coverage ratio over a longer amortization window.",
  },
  {
    q: "Can I pay principal voluntarily during the interest-only period?",
    a: "Most programs allow additional principal payments during the IO period without penalty, though check your specific note and prepayment structure — a step-down prepay can apply to large curtailments in some cases.",
  },
  {
    q: "Does interest-only work on short-term rentals?",
    a: "Yes on select programs, though short-term rental leverage already caps around 75% LTV, so pairing it with interest-only is most useful for maximizing coverage ratio rather than pushing leverage further.",
  },
  {
    q: "How much lower is the payment with interest-only versus fully amortizing?",
    a: "The reduction depends on rate and term, but on a 30-year note the interest-only payment is typically meaningfully lower than the amortizing payment because none of the payment is reducing principal during the IO period.",
  },
  {
    q: "Can I refinance out before the interest-only period ends?",
    a: "Yes, and many investors plan to do exactly that — refinance, sell, or otherwise exit before the recast date to avoid the payment step-up that occurs once the loan begins amortizing over the remaining term.",
  },
  {
    q: "Does interest-only affect my prepayment penalty?",
    a: "The prepayment penalty structure is a separate term you select independently of interest-only, though some programs pair specific prepay options with the IO feature, so review both terms together on the term sheet.",
  },
];

export const Route = createFileRoute("/interest-only-dscr-loans")({
  head: () =>
    buildHead({
      title: "Interest-Only DSCR Loans: Lower Payment, Higher Ratio",
      description:
        "Interest-only DSCR loans for rental property — how a 10-year IO period lowers PITIA, raises your DSCR, and what the rate adjustment and recast actually cost you.",
      url: URL,
      keywords: KEYWORDS,
      image: HERO?.src,
      imageAlt: HERO?.alt,
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "DSCR Loans", url: "/dscr-loans" },
        { name: "Interest-Only DSCR", url: URL },
      ],
      service: { name: "Interest-Only DSCR Rental Loan" },
      faqs: FAQS,
    }),
  component: InterestOnlyDscr,
});

function InterestOnlyDscr() {
  return (
    <SiteShell hideBreadcrumbs productLabel="Interest-Only DSCR">
      <PageHero
        eyebrow="DSCR · Structure"
        title="Interest-only DSCR loans"
        subtitle="An interest-only period lowers the payment the lender divides your rent by. Same property, same rent — a higher ratio, better cash flow, and often a better pricing tier."
        image={HERO}
        primaryHref="/apply?product=dscr"
      />
      <RankKit page="interest-only-dscr-loans" part="answer" />
      <PageEssentials hideAnswer hideTldr hideToc />
      <Breadcrumbs items={[{ name: "Home", href: "/" }, { name: "DSCR Loans", href: "/dscr-loans" }, { name: "Interest-Only" }]} />

      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#math", label: "The math" },
          { href: "#fit", label: "Where it fits" },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <Section id="tldr">
        <TLDR
          points={[
            "Pay interest only for an initial period — commonly 10 years — then the loan amortizes over the remaining term.",
            "Most programs qualify the file on the interest-only payment, which raises the DSCR.",
            "Expect a modest rate adjustment and a slightly higher credit floor than the amortizing version.",
            "Reserves are unchanged at 1–6 months of PITIA.",
            "Plan the exit: refinance, sale, or rent growth before the payment recasts.",
          ]}
        />
        <div className="mt-8">
          <StatRow
            items={[
              { label: "Typical IO period", value: "10 years" },
              { label: "Term options", value: "30 or 40 yr" },
              { label: "Qualifying payment", value: "IO payment" },
              { label: "Reserves", value: "1–6 mo PITIA" },
            ]}
          />
        </div>
      </Section>

      <Section id="math">
        <SectionHeader
          eyebrow="The math"
          title="What the interest-only payment does to your ratio"
          intro="The rent does not change. Only the denominator does — and the denominator is what decides your tier."
        />
        <div className="mt-8 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full min-w-[620px] text-sm">
            <thead className="bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Structure</th>
                <th className="px-4 py-3">Monthly rent</th>
                <th className="px-4 py-3">PITIA</th>
                <th className="px-4 py-3">DSCR</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              <tr>
                <td className="px-4 py-3 font-semibold text-foreground">Fully amortizing</td>
                <td className="px-4 py-3 text-foreground">$2,400</td>
                <td className="px-4 py-3 text-foreground">$2,450</td>
                <td className="px-4 py-3 text-muted-foreground">0.98</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-semibold text-foreground">Interest-only</td>
                <td className="px-4 py-3 text-foreground">$2,400</td>
                <td className="px-4 py-3 text-foreground">$2,080</td>
                <td className="px-4 py-3 text-brand">1.15</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Illustrative example only, not a quote. Actual payments, ratios, and pricing depend on the rate, taxes,
          insurance, association dues, and program in effect at the time of application. Not a commitment to lend.
        </p>
        <div className="mt-10">
          <DscrCalculator productLabel="Interest-Only DSCR" />
        </div>
      </Section>

      <div className="container mx-auto max-w-7xl px-4">
        <ProductReportCtaBanner productKey="dscr" variant="inline" />
      </div>

      <div className="bg-muted/40">
        <Section id="fit">
          <SectionHeader eyebrow="Where it earns its keep" title="Common reasons investors choose interest-only" />
          <div className="mt-10">
            <FeatureGrid
              items={[
                { title: "Cash flow now", body: "The lower payment frees monthly cash for reserves, capital improvements, or the down payment on the next property." },
                { title: "Marginal ratios", body: "A deal that misses 1.00 on an amortizing payment can clear it comfortably on the interest-only payment." },
                { title: "Value-add holds", body: "Keep the payment light while units turn and rents reset, then refinance into the amortizing structure." },
                { title: "Portfolio scaling", body: "More free cash flow per door compounds faster than principal paydown when you are still acquiring." },
                { title: "Cash-out refinance", body: "Pull equity while keeping the new payment close to what you were already carrying." },
                { title: "40-year terms", body: "Where available, a 40-year term with a 10-year IO period stretches the ratio further still." },
              ]}
            />
          </div>
        </Section>
      </div>

      <LiveDscrRateSection
            strategyCards
        scenarioOverrides={{ interestOnly: true }}
        toolLabel="Interest-only DSCR"
        id="live-rates"
        eyebrow={"Live pricing · Interest-only"}
        heading={"Current Interest-Only DSCR Rates"}
        paragraphs={[
            "An interest-only payment is lower than the equivalent amortizing payment, so where a program qualifies on the interest-only payment the coverage ratio calculates higher. Whether a program does so varies by investor. The live options below are priced as fully amortizing quotes; the monthly figure on each row is labeled so you always know whether you are looking at principal-and-interest or an interest-only payment.",
            "Interest-only availability, the payment used to qualify and how the structure prices are all program-specific and differ between investors. The structural trade is monthly cash flow now against no principal reduction during the interest-only period.",
        ]}
        faqs={[
            { q: "Does interest-only always improve my DSCR?", a: "It improves the ratio whenever qualification uses the interest-only payment, which is program-specific. Some lenders qualify on the fully amortizing payment even when the note is interest-only." },
            { q: "What happens when the interest-only period ends?", a: "The loan recasts and amortizes over the remaining term, which raises the payment. Plan the exit, refinance or amortized payment before the recast date." },
        ]}
        links={[
            { to: "/investment-property-rates", label: "Price an amortizing DSCR scenario" },
            { to: "/dscr-calculator", label: "Model the ratio both ways" },
            { to: "/how-dscr-works", label: "How the DSCR calculation works" },
        ]}
      />

      <ProsCons
        title="Interest-only, honestly"
        pros={[
          "Lower payment and a materially higher qualifying DSCR",
          "More monthly cash flow while you scale",
          "Can unlock leverage the amortizing payment would not support",
          "Available on purchase and, on many programs, cash-out refinance",
        ]}
        cons={[
          "No principal paydown during the IO period — equity comes only from appreciation",
          "A rate adjustment versus the amortizing version of the same program",
          "Higher credit floor and slightly lower maximum leverage",
          "The payment recasts at the end of the IO period, so the exit has to be planned",
        ]}
      />

      <InlineCTA
        variant="soft"
        title="See both structures side by side"
        body="Send the address and rent — we'll run the amortizing and interest-only versions so you can compare ratio, payment, and pricing."
        href="/apply?product=dscr"
      />


      <DscrProgramLinks currentPath={URL} />

      <RankKit page="interest-only-dscr-loans" part="details" />
      <Section id="faq">
        <SectionHeader eyebrow="FAQ" title="Interest-only DSCR questions" />
        <div className="mt-8">
          <FAQ items={FAQS} />
        </div>
        <p className="mt-8 text-xs text-muted-foreground">
          Guidelines shown are illustrative and vary by lender, property, and state. Eligibility, terms, and availability
          are subject to underwriting and approval. Not a commitment to lend. Business-purpose loans only.
        </p>
      </Section>

      <DscrCrossProductLinks currentPath={URL} />

      <ExpertInsight insightKey="interest-only-dscr" />

      <CTABand
        title="Price an interest-only DSCR on your property"
        body="A short pre-qualification request is all it takes to get a scenario review."
        href="/apply?product=dscr"
      />
    <PageOutro hideInsight />
      </SiteShell>
  );
}
